import {
  FlowModuleItemType,
  ModuleIOValueTypeEnum,
} from "@packages/flow-core/module";
import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import styles from "./index.less";
import {
  Button,
  Divider,
  Tooltip,
  message,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Space,
  Radio,
  InputNumber,
  FormInstance,
} from "antd";
import {
  DeleteOutlined,
  HolderOutlined,
  MenuOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  EditNodeFieldType,
  FlowNodeInputItemType,
} from "@packages/flow-core/module/node/types";
import { FC, useRef, memo, useCallback, useMemo, useState } from "react";
import { FlowNodeInputTypeEnum } from "@packages/flow-core/module/node/constants";
import { useFlow } from "../FlowEdit/useFlow";
import { FlowValueTypeMap } from "../../../constants/modules/dataType";
import {
  DndContext,
  useSensor,
  PointerSensor,
  useSensors,
  DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { nanoid } from "nanoid";
import classNames from "classnames";
import { MaterialTypeOptions } from "../../services/template";

// 自定义 transform 处理函数，避免不等高元素拖拽时被压扁
const createTransformStyle = (
  transform: any,
  transition: string | undefined
) => {
  if (!transform) return { transition };

  // 只使用 translate，忽略 scaleX 和 scaleY
  return {
    transition,
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  };
};

const SortableItem = ({ children, id }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id });

  // 使用自定义函数处理 transform
  const style = createTransformStyle(transform, transition);

  return (
    <Space
      style={{ ...style, width: "100%" }}
      ref={setNodeRef}
      {...attributes}
      align="baseline"
    >
      {children}
      <MenuOutlined
        ref={setActivatorNodeRef}
        {...listeners}
        style={{ cursor: "grab" }}
      />
    </Space>
  );
};

const SelectionOption = (props: {
  value?: { label: string; value: string; key: string }[];
  onChange?: (value: { label: string; value: string; key: string }[]) => void;
}) => {
  const { value = [], onChange } = props;
  const sensors = useSensors(useSensor(PointerSensor));
  function handleDragEnd({ active, over }: DragEndEvent) {
    if (active.id !== over?.id) {
      const oldIndex = value.findIndex((item) => item.key === active.id);
      const newIndex = value.findIndex((item) => item.key === over?.id);
      onChange?.(arrayMove(value, oldIndex, newIndex));
    }
  }

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
    >
      <SortableContext
        items={value.map((field) => field.key)}
        strategy={verticalListSortingStrategy}
      >
        {value.map((field, index) => {
          return (
            <SortableItem key={field.key} id={field.key}>
              <MinusCircleOutlined
                onClick={() => {
                  onChange?.(value.filter((item) => item.key !== field.key));
                }}
                style={
                  index === 0
                    ? { visibility: "hidden" }
                    : { visibility: "visible" }
                }
              />

              <Input
                value={field.label}
                onChange={(e) => {
                  onChange?.(
                    value.map((item) =>
                      item.key === field.key
                        ? {
                            ...item,
                            label: e.target.value,
                            value: e.target.value,
                          }
                        : item
                    )
                  );
                }}
              />
            </SortableItem>
          );
        })}
      </SortableContext>
      <Button
        icon={<PlusOutlined />}
        type="dashed"
        onClick={() => {
          onChange?.(
            value.concat({
              value: `选项${value.length + 1}`,
              label: `选项${value.length + 1}`,
              key: nanoid(),
            })
          );
        }}
        block
      >
        添加下拉选项
      </Button>
    </DndContext>
  );
};

interface SortableParamInputItemProps {
  key: React.Key;
  moduleId: string;
  input: FlowNodeInputItemType;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEditFieldValue: React.Dispatch<
    React.SetStateAction<EditNodeFieldType | undefined>
  >;
}

const SortableParamInputItem: FC<SortableParamInputItemProps> = ({
  key,
  moduleId,
  input,
  setOpen,
  setEditFieldValue,
}) => {
  const { deleteModuleInput, deleteModuleOutput } = useFlow();
  const {
    attributes,
    listeners,
    transform,
    transition,
    setNodeRef,
    setActivatorNodeRef,
  } = useSortable({
    id: input.key,
  });

  // 使用自定义函数处理 transform
  const style = createTransformStyle(transform, transition);

  return (
    <div
      key={key}
      ref={setNodeRef}
      className={`${styles["mb-7"]} ${styles["pos-rel"]}`}
      style={style}
      {...attributes}
    >
      <HolderOutlined
        {...listeners}
        title="拖动进行参数排序"
        ref={setActivatorNodeRef}
        className={classNames(styles["param-input-sort"])}
      />
      {input.required && <span className={styles.required}>*</span>}
      <span>{input.label}</span>
      {input.description && (
        <Tooltip title={input.description}>
          <QuestionCircleOutlined className={styles["ml-4"]} />
        </Tooltip>
      )}
      <SettingOutlined
        className={styles["ml-4"]}
        onClick={() => {
          setEditFieldValue({
            valueType: input.valueType,
            required: input.required,
            label: input.label,
            key: input.key,
            description: input.description,
            defaultValue: input.value as string,
            inputType: input.type,
            list: input.list,
            parameterStage: input.parameterStage,
            materialType: input.materialType,
          });
          setOpen(true);
        }}
      />
      <DeleteOutlined
        className={styles["ml-4"]}
        onClick={() => {
          deleteModuleInput(moduleId, input.key);
          deleteModuleOutput(moduleId, input.key);
        }}
      />
    </div>
  );
};

const InputTypeSelectOptions = [
  {
    label: "单行输入框",
    value: FlowNodeInputTypeEnum.input,
  },
  {
    label: "多行输入框",
    value: FlowNodeInputTypeEnum.textarea,
  },
  {
    label: "下拉菜单",
    value: FlowNodeInputTypeEnum.select,
  },
  {
    label: "企微成员选择框",
    value: FlowNodeInputTypeEnum.selectWxUser,
  },
  {
    label: "机器人选择框",
    value: FlowNodeInputTypeEnum.selectAppName,
  },
  {
    label: "12Times 标签选择框",
    value: FlowNodeInputTypeEnum.selectDamselfishTags,
  },
];

export type FieldEditModalProps = {
  visible?: boolean;
  onConfirm: (data: EditNodeFieldType) => void;
  onClose: () => void;
  data?: EditNodeFieldType;
  externalDataTypeSelectList?: {
    label: string;
    value: ModuleIOValueTypeEnum;
  }[];
};
function FieldEditModal({
  visible,
  onClose,
  onConfirm,
  data,
  externalDataTypeSelectList,
}: FieldEditModalProps) {
  const formRef = useRef<FormInstance>(null);

  const dataTypeSelectList = useMemo(
    () =>
      externalDataTypeSelectList ??
      Object.values(FlowValueTypeMap)
        .filter(Boolean)
        .map((item) => ({
          label: item!.label,
          value: item!.value,
        })),
    [externalDataTypeSelectList]
  );

  async function handleConfirm() {
    const values = await formRef.current?.validateFields()!;
    onConfirm({
      ...values,
      inputType:
        values.valueType === ModuleIOValueTypeEnum.string
          ? values.inputType
          : values.valueType === ModuleIOValueTypeEnum.number
          ? FlowNodeInputTypeEnum.numberInput
          : FlowNodeInputTypeEnum.switch,
      defaultValue: values.defaultValue,
    });
  }

  const initialValues = useMemo(() => {
    if (!data) return { valueType: ModuleIOValueTypeEnum.string };
    return data;
  }, [data]);

  return (
    <Modal
      open={visible}
      okText="确认"
      cancelText="取消"
      onCancel={onClose}
      onOk={handleConfirm}
      title="字段编辑"
      destroyOnClose={true}
      width={600}
    >
      <Form
        ref={formRef}
        labelCol={{ span: 5 }}
        colon={false}
        initialValues={initialValues}
      >
        <Form.Item
          label="数据类型"
          name="valueType"
          rules={[{ required: true }]}
        >
          <Select
            options={dataTypeSelectList}
            onChange={(v) =>
              formRef.current?.setFieldsValue({
                inputType: undefined,
                defaultValue:
                  v === ModuleIOValueTypeEnum.boolean ? false : undefined,
              })
            }
          />
        </Form.Item>
        <Form.Item
          label="是否必填"
          valuePropName="checked"
          name="required"
          rules={[{ required: true }]}
          initialValue={false}
        >
          <Switch />
        </Form.Item>
        <Form.Item label="字段名称" name="label" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="字段 Key" name="key" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="字段描述" name="description">
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          label="参数所属阶段"
          name="parameterStage"
          rules={[{ required: true, message: "请选择参数所属阶段" }]}
        >
          <Select
            options={[
              { label: "基础", value: "BASIC" },
              { label: "工作流", value: "WORKFLOW" },
            ]}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldsValue }) => {
            const { valueType, parameterStage } = getFieldsValue();
            if (!valueType) return null;

            if (valueType === ModuleIOValueTypeEnum.string) {
              return (
                <>
                  <Form.Item
                    label="创建时控件"
                    name="inputType"
                    rules={[{ required: true, message: "请选择创建时控件" }]}
                    key="string"
                  >
                    <Select
                      options={
                        parameterStage === "BASIC"
                          ? InputTypeSelectOptions
                          : [
                              ...InputTypeSelectOptions,
                              {
                                label: "文件上传框",
                                value: FlowNodeInputTypeEnum.uploadMaterial,
                              },
                            ]
                      }
                    />
                  </Form.Item>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldsValue }) => {
                      const { inputType } = getFieldsValue();

                      if (inputType === FlowNodeInputTypeEnum.select) {
                        return (
                          <>
                            <Form.Item
                              label="下拉选项"
                              name="list"
                              initialValue={[
                                {
                                  value: "选项1",
                                  label: "选项1",
                                  key: nanoid(),
                                },
                              ]}
                              rules={[
                                {
                                  validator(rule, value: { value: string }[]) {
                                    if (value.some((item) => !item.value)) {
                                      return Promise.reject("下拉选项不能为空");
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <SelectionOption />
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldsValue }) => {
                                const { list } = getFieldsValue();
                                return (
                                  <Form.Item label="默认值" name="defaultValue">
                                    <Radio.Group>
                                      {list?.map((item: any) => (
                                        <Radio
                                          value={item.value}
                                          key={item.key}
                                        >
                                          {item.label}
                                        </Radio>
                                      ))}
                                    </Radio.Group>
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </>
                        );
                      }

                      if (inputType === FlowNodeInputTypeEnum.textarea) {
                        return (
                          <Form.Item label="默认值" name="defaultValue">
                            <Input.TextArea />
                          </Form.Item>
                        );
                      }
                      if (inputType === FlowNodeInputTypeEnum.input) {
                        return (
                          <Form.Item label="默认值" name="defaultValue">
                            <Input />
                          </Form.Item>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>

                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldsValue }) => {
                      const { inputType, parameterStage } = getFieldsValue();
                      if (
                        inputType === FlowNodeInputTypeEnum.uploadMaterial &&
                        parameterStage === "WORKFLOW"
                      ) {
                        return (
                          <Form.Item
                            label="文件类型"
                            name="materialType"
                            rules={[
                              { required: true, message: "请选择文件类型" },
                            ]}
                          >
                            <Select options={MaterialTypeOptions} />
                          </Form.Item>
                        );
                      }

                      return null;
                    }}
                  </Form.Item>
                </>
              );
            }

            return null;
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldsValue }) => {
            const { valueType } = getFieldsValue();

            if (valueType === ModuleIOValueTypeEnum.number) {
              return (
                <Form.Item label="默认值" name="defaultValue">
                  <InputNumber />
                </Form.Item>
              );
            }

            if (valueType === ModuleIOValueTypeEnum.boolean) {
              return (
                <Form.Item label="默认值" name="defaultValue">
                  <Select
                    options={[
                      { label: "true", value: true },
                      { label: "false", value: false },
                    ]}
                  />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default memo(function NodeTemplateParam({
  data,
  selected,
}: NodeProps<FlowModuleItemType>) {
  const { moduleId, inputs } = data;
  const [open, setOpen] = useState(false);
  const {
    addModuleInput,
    updateModuleInput,
    delEdge,
    getAllEdges,
    updateModule,
  } = useFlow();

  const [editFieldValue, setEditFieldValue] = useState<EditNodeFieldType>();

  const limitedDataTypeSelectList = useMemo(
    () =>
      Object.values(FlowValueTypeMap)
        .filter(
          (item) =>
            item?.value === ModuleIOValueTypeEnum.string ||
            item?.value === ModuleIOValueTypeEnum.number ||
            item?.value === ModuleIOValueTypeEnum.boolean
        )
        .map((item) => ({
          label: item!.label,
          value: item!.value,
        })),
    []
  );

  const getInputTypeByValueType = useCallback(
    (valueType: `${ModuleIOValueTypeEnum}`) => {
      switch (valueType) {
        case ModuleIOValueTypeEnum.string: {
          return FlowNodeInputTypeEnum.textarea;
        }
        case ModuleIOValueTypeEnum.number: {
          return FlowNodeInputTypeEnum.numberInput;
        }
        case ModuleIOValueTypeEnum.boolean: {
          return FlowNodeInputTypeEnum.switch;
        }
        default: {
          // 其他的回滚
          return FlowNodeInputTypeEnum.textarea;
        }
      }
    },
    []
  );

  return (
    <>
      <NodeCard minWidth={300} selected={selected} forbitMenu {...data}>
        <Divider style={{ margin: 0 }} />
        <div className={classNames(styles.content, "nodrag")}>
          <DndContext
            modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (active.id !== over?.id) {
                const inputsCopied = JSON.parse(
                  JSON.stringify(inputs)
                ) as FlowNodeInputItemType[];

                const activeIndex = inputsCopied.findIndex(
                  (i) => i.key === active.id
                );
                const overIndex = inputsCopied.findIndex(
                  (i) => i.key === over?.id
                );

                const sorted = arrayMove(
                  inputsCopied,
                  activeIndex,
                  overIndex
                ).map((g, idx) => ({
                  ...g,
                  sort: idx,
                }));

                // 更新排序
                updateModule(moduleId, { ...data, inputs: sorted });
              }
            }}
          >
            <SortableContext
              items={inputs.map((item) => item.key)}
              strategy={verticalListSortingStrategy}
            >
              {inputs.map((input) => {
                return (
                  <SortableParamInputItem
                    key={input.key}
                    input={input}
                    moduleId={moduleId}
                    setOpen={setOpen}
                    setEditFieldValue={setEditFieldValue}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          <Button
            style={{
              marginTop: 10,
            }}
            icon={<PlusOutlined />}
            onClick={() => {
              setEditFieldValue(undefined);
              setOpen(true);
            }}
          >
            添加参数
          </Button>
        </div>
      </NodeCard>
      <FieldEditModal
        externalDataTypeSelectList={limitedDataTypeSelectList}
        onClose={() => {
          setOpen(false);
        }}
        data={editFieldValue}
        onConfirm={(data) => {
          const newInput: FlowNodeInputItemType = {
            type:
              data.inputType ??
              getInputTypeByValueType(data.valueType ?? "string"),
            valueType: data.valueType ?? "string",
            key: data.key!,
            label: data.label!,
            description: data.description ?? "",
            value: data.defaultValue ?? "",
            required: data.required,
            showTargetInApp: false,
            showTargetInPlugin: false,
            list: data.list,
            parameterStage: data.parameterStage,
            materialType: data.materialType,
            edit: true,
          };
          if (Object.keys(editFieldValue || {}).length !== 0) {
            if (editFieldValue!.key !== newInput.key) {
              const existed = inputs.find((item) => item.key === newInput.key);
              if (existed) {
                message.warning("字段Key不可重复");
                return;
              }
            }
            if (editFieldValue!.valueType !== newInput.valueType) {
              // 删除已经绘制的线
              getAllEdges()
                .filter(
                  (edge) =>
                    edge.source === moduleId &&
                    edge.sourceHandle === newInput.key
                )
                .forEach((edge) => {
                  delEdge(edge.id);
                });
            }
            updateModuleInput(moduleId, editFieldValue!.key!, newInput);
          } else {
            const existed = inputs.find((item) => item.key === newInput.key);
            if (existed) {
              message.warning("字段Key不可重复");
              return;
            }
            addModuleInput(moduleId, newInput);
          }
          setOpen(false);
        }}
        visible={open}
      />
    </>
  );
});
